/** CRM / Painel de Oportunidades — domain types (Sprint 0.7) */

export type OpportunityStage =
  | 'DRAFT'
  | 'SENT'
  | 'AWAITING'
  | 'NEGOTIATION'
  | 'APPROVED'
  | 'LOST'
  | 'CANCELLED'

export type OpportunityStatus = OpportunityStage

export type ContactType = 'whatsapp' | 'phone' | 'email' | 'visit' | 'other'

export type FollowUpResult =
  | 'APPROVED'
  | 'STILL_THINKING'
  | 'BOUGHT_ELSEWHERE'
  | 'CANCELLED'
  | 'NO_RESPONSE'

export type LostReasonCode = 'PRICE' | 'DEADLINE' | 'BRAND' | 'SERVICE' | 'OTHER'

export type ReminderStatus = 'PENDING' | 'SHOWN' | 'DONE' | 'DISMISSED'

export type ReminderLevel = 'info' | 'warning' | 'urgent'

export type CrmPermission = 'view' | 'edit' | 'delete'

export interface Opportunity {
  id: string
  quoteId: string
  quoteCode: string
  customerId: string | null
  customerName: string | null
  customerPhone: string | null
  customerCity: string | null
  status: OpportunityStatus
  stage: OpportunityStage
  probability: number
  expectedValue: number
  expectedCloseDate: string | null
  lastContact: string | null
  nextContact: string | null
  responsibleUser: string
  notes: string
  lostReasonId: string | null
  lostReasonCode: LostReasonCode | null
  lostReasonNotes: string | null
  daysIdle: number
  createdAt: string
  updatedAt: string
}

export interface FollowUp {
  id: string
  opportunityId: string
  date: string
  contactType: ContactType
  notes: string
  result: FollowUpResult | null
  createdBy: string
  createdAt: string
}

export interface Reminder {
  id: string
  opportunityId: string
  date: string
  message: string
  status: ReminderStatus
  level: ReminderLevel
  daysIdle: number
  createdAt: string
}

export interface LostReason {
  id: string
  description: string
  code: LostReasonCode | 'CUSTOM'
  active: boolean
}

export interface TimelineEvent {
  id: string
  opportunityId: string
  type: string
  label: string
  at: string
  meta?: string | null
}

export interface OpportunityFilters {
  periodFrom?: string | null
  periodTo?: string | null
  status?: OpportunityStage | 'ALL' | null
  responsibleUser?: string
  city?: string
  customer?: string
  query?: string
}

export interface CrmPermissions {
  view: boolean
  edit: boolean
  delete: boolean
}

export interface PipelineColumn {
  stage: OpportunityStage
  label: string
  opportunities: Opportunity[]
  totalValue: number
}

export interface CrmDashboardCards {
  totalOpportunities: number
  totalValue: number
  awaitingResponse: number
  approved: number
  lost: number
}

export interface CrmStatistics {
  cards: CrmDashboardCards
  conversionRate: number
  averageTicket: number
  averageCycleDays: number
  lostValue: number
  recoveredValue: number
  funnel: Array<{ stage: OpportunityStage; label: string; count: number; value: number }>
  lossesByReason: Array<{ reason: string; count: number; value: number }>
  ranking: {
    customers: Array<{ name: string; value: number; count: number }>
    products: Array<{ name: string; value: number; count: number }>
    categories: Array<{ name: string; value: number; count: number }>
    sellers: Array<{ name: string; value: number; count: number }>
  }
}

export interface WhatsappMessageDraft {
  opportunityId: string
  customerName: string
  phone: string | null
  body: string
  channel: 'whatsapp'
  preparedFor: 'whatsapp-api'
}

export const PIPELINE_STAGES: OpportunityStage[] = [
  'DRAFT',
  'SENT',
  'AWAITING',
  'NEGOTIATION',
  'APPROVED',
]

export const ALL_STAGES: OpportunityStage[] = [
  ...PIPELINE_STAGES,
  'LOST',
  'CANCELLED',
]

export const STAGE_LABELS: Record<OpportunityStage, string> = {
  DRAFT: 'Rascunho',
  SENT: 'Enviado',
  AWAITING: 'Aguardando Cliente',
  NEGOTIATION: 'Negociação',
  APPROVED: 'Aprovado',
  LOST: 'Perdido',
  CANCELLED: 'Cancelado',
}

export const FOLLOW_UP_RESULT_LABELS: Record<FollowUpResult, string> = {
  APPROVED: 'Aprovou',
  STILL_THINKING: 'Ainda pensando',
  BOUGHT_ELSEWHERE: 'Comprou em outro lugar',
  CANCELLED: 'Cancelou',
  NO_RESPONSE: 'Sem resposta',
}

export const LOST_REASON_LABELS: Record<LostReasonCode, string> = {
  PRICE: 'Preço',
  DEADLINE: 'Prazo',
  BRAND: 'Marca',
  SERVICE: 'Atendimento',
  OTHER: 'Outro',
}

export const DEFAULT_LOST_REASONS: Array<Omit<LostReason, 'id'>> = [
  { description: 'Preço', code: 'PRICE', active: true },
  { description: 'Prazo', code: 'DEADLINE', active: true },
  { description: 'Marca', code: 'BRAND', active: true },
  { description: 'Atendimento', code: 'SERVICE', active: true },
  { description: 'Outro', code: 'OTHER', active: true },
]

export const DEFAULT_CRM_PERMISSIONS: CrmPermissions = {
  view: true,
  edit: true,
  delete: true,
}

export const STAGE_PROBABILITY: Record<OpportunityStage, number> = {
  DRAFT: 10,
  SENT: 25,
  AWAITING: 40,
  NEGOTIATION: 60,
  APPROVED: 100,
  LOST: 0,
  CANCELLED: 0,
}
